import React, { useState } from "react";
import { Card, Upload, Spin, Select } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from "../components/ProtectedToolWrapper";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";

const PdfToCsv = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [page, setPage] = useState(null);
  const [csvData, setCsvData] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslation();

  const extractPdfMeta = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const typedArray = new Uint8Array(reader.result);
      const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
      setTotalPages(pdf.numPages);
      setPage(1);
    };
    reader.readAsArrayBuffer(file);
  };

  const beforeUpload = async (file) => {
    if (file.type !== "application/pdf") {
      toast.error(t("upload_only_pdf"));
      return false;
    }

    setPdfFile(file);
    setPdfPreviewUrl(URL.createObjectURL(file));
    extractPdfMeta(file);
    setCsvData("");
    return false;
  };

  const handleExtractCsv = async () => {
    if (!pdfFile || !page) return;
    setLoading(true);
    const toastId = toast.loading(t("extracting_csv"));
    setCsvData("");

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const typedArray = new Uint8Array(reader.result);
          const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
          const pdfPage = await pdf.getPage(page);

          const viewport = pdfPage.getViewport({ scale: 2 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await pdfPage.render({ canvasContext: context, viewport }).promise;

          const dataUrl = canvas.toDataURL("image/png");
          const imageBlob = await fetch(dataUrl).then((r) => r.blob());

          const formData = new FormData();
          formData.append("image", imageBlob, "page.png");
          formData.append("output_format", "csv");

          const res = await api.post(`/ocr-image`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (res.data.text) {
            setCsvData(res.data.text);
            toast.success(t("csv_extracted_success"));
          } else {
            throw new Error(t("ocr_failed"));
          }
        } catch (err) {
          if (err?.response?.data?.error?.includes("Insufficient credits")) {
            toast.error(t("insufficient_credits_error"));
          } else {
            toast.error(t("ocr_failed"));
          }
        } finally {
          toast.dismiss(toastId);
          setLoading(false);
        }
      };
      reader.onerror = () => toast.error(t("ocr_failed"));
      reader.readAsArrayBuffer(pdfFile);
    } catch (err) {
      toast.error(err.message || t("server_error"));
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(csvData);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("copy_failed"));
    }
  };

  const handleDownloadCsv = () => {
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `page-${page}-data.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          {!pdfFile && (
            <div className="min-h-[70vh] flex items-center justify-center">
              <Upload beforeUpload={beforeUpload} showUploadList={false} accept=".pdf">
                <StandardButton
                  size="large"
                  className="px-6 py-3 text-lg"
                  type="secondary"
                >
                  {t("upload_pdf_button")}
                </StandardButton>
              </Upload>
            </div>
          )}

          {pdfPreviewUrl && (
            <>
              <iframe 
                src={pdfPreviewUrl} 
                title="PDF Preview" 
                className="w-full h-[600px] border rounded shadow" 
              />
              
              <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
                <div className="w-full md:w-1/3">
                  <Select
                    className="w-full"
                    value={page}
                    onChange={setPage}
                    placeholder={t("choose_page_placeholder")}
                  >
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Select.Option key={i + 1} value={i + 1}>
                        {t("page_label")} {i + 1}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  <StandardButton
                    onClick={handleExtractCsv}
                    className="px-4 py-2"
                    type="secondary"
                  >
                    {t("extract_csv_button")}
                  </StandardButton>
                </div>
              </div>
            </>
          )}

          {csvData && (
            <div className="mt-6 bg-white p-4 border rounded shadow relative">
              <h3 className="font-semibold mb-2">{t("extracted_csv_label")}</h3>
              <div className="max-h-96 overflow-auto">
                <pre className="text-sm bg-gray-50 p-3 rounded border whitespace-pre-wrap font-mono">
                  {csvData}
                </pre>
              </div>
              <div className="mt-4 flex gap-3">
                <StandardButton
                  onClick={handleCopy}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("copy_button")}
                </StandardButton>
                <StandardButton
                  onClick={handleDownloadCsv}
                  type="secondary"
                  size="small"
                >
                  {t("download_csv_button")}
                </StandardButton>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center">
              <Spin tip={t("processing")} />
            </div>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default PdfToCsv;